import { useMemo } from 'react'

import { CartDataInput, GeoPointInput } from '@shared/generated'

import { CartProduct } from '../model/useCartStore'

type CartInfoCart = {
  items: CartProduct[];
  promocode?: string;
}

type CartSalePromocodeInput = {
  code: string;
}

type CartSaleConfigInput = {
  type: string;
  promocodes?: CartSalePromocodeInput[];
}

type CartInfoInputRequest = {
  shop_id: string;
  zone_id?: string;
  items: CartDataInput[];
  deliveryType?: string;
  customerGeoPoint?: GeoPointInput;
  sale?: CartSaleConfigInput;
}

export type CartInfoRequestVariables = {
  cartInfo: CartInfoInputRequest;
}

interface BuildCartInfoVariablesParams {
  shopId?: string | null;
  zoneId?: string | null;
  deliveryType?: string | null;
  customerGeoPoint?: GeoPointInput | null;
  cart?: CartInfoCart;
  clearPromocode?: boolean;
}

export function buildCartInfoVariables ({
  shopId,
  zoneId,
  deliveryType,
  customerGeoPoint,
  cart,
  clearPromocode = false,
}: BuildCartInfoVariablesParams): CartInfoRequestVariables | null {
  if (!shopId) {
    return null
  }

  const promocode = cart?.promocode?.trim()

  return {
    cartInfo: {
      shop_id: shopId,
      zone_id: zoneId || undefined,
      items: cart?.items
        ?.filter((item) => item.amount > 0)
        .map(({ product_id, amount }) => ({
          product_id,
          amount,
        })) ?? [],
      deliveryType: deliveryType || undefined,
      customerGeoPoint: customerGeoPoint ?? undefined,
      sale: clearPromocode
        ? {
          type: 'promocode',
          promocodes: [],
        }
        : promocode
        ? {
          type: 'promocode',
          promocodes: [ { code: promocode } ],
        }
        : undefined,
    },
  }
}

export function useCartInfoVariables (params: BuildCartInfoVariablesParams) {
  const { shopId, zoneId, deliveryType, customerGeoPoint, cart, clearPromocode } = params

  return useMemo(
    () => buildCartInfoVariables({
      shopId,
      zoneId,
      deliveryType,
      customerGeoPoint,
      cart,
      clearPromocode,
    }),
    [ cart, clearPromocode, customerGeoPoint, deliveryType, shopId, zoneId ],
  )
}
